import { acceptHMRUpdate, defineStore } from 'pinia'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { GeoJsonLayer } from '@deck.gl/layers'
import {
    getMachinesAndBoomsFromTask,
    getAsAppliedMapEntriesFromTask,
} from '@/scripts/task'
import { useMaps } from './maps'
import { useYieldMap } from './yieldMap'
import { useIsoPreview } from './isoPreview'
import { useFieldLayersStore } from './fieldLayers'
import { useVehiclesLiveStore } from './vehiclesLive'
import { useDemoStore } from './demoStore'
import { hexToRgba } from '@/helpers/yieldMapBuckets'
import { getRateHex, toDisplayRate } from '@/helpers/asAppliedMapRate'
import { formatDate } from '@/helpers/dateHelper'
import i18n from '@/i18n'

const AS_APPLIED_POLL_INTERVAL_MS = 30000
/** When true, poll as-applied only if task status is `running`. When false, always poll after show. */
const AS_APPLIED_POLL_ONLY_WHEN_RUNNING = false

export const useAsAppliedMap = defineStore('asAppliedMap', {
    state: () => ({
        isUnmounted: false,
        loading: false,
        mapLoading: false,
        mapLoadingKey: null,
        machinesAndBooms: {},
        featureCollection: null,
        activeFilter: null,
        visible: false,
        overlay: null,
        layer: null,
        hoverFeature: null,
        legendDraggable: null,
        pollIntervalId: null,
        pollInFlight: false,
    }),
    getters: {
        isWorkingSpeed: (state) => state.activeFilter?.boomId === 0 && state.activeFilter?.boomName === '-',
    },
    actions: {
        async getMachinesAndBooms(taskId) {
            const demo = useDemoStore()
            if (demo.isBlocked('as_applied')) return false

            if (this.isUnmounted) return false

            this.loading = true
            try {
                const res = await getMachinesAndBoomsFromTask(taskId)
                if (this.isUnmounted) return false

                if (res.ok) {
                    this.machinesAndBooms = res.data.reduce((acc, item) => {
                        const nexatMachineId = item.nexat_machine_id
                        if (!acc[nexatMachineId]) {
                            acc[nexatMachineId] = []
                        }
                        acc[nexatMachineId].push({
                            machineId: item.machine_id,
                            boomId: item.boom_id,
                            implementName: item.implement_name ?? '-',
                            boomName: item.boom_name ?? '-',
                        })
                        return acc
                    }, {})
                    return true
                }
                return false
            } catch (error) {
                console.error('Error getting machines and booms from task:', error)
                return false
            } finally {
                this.loading = false
            }
        },
        mapEntryKey(nexatMachineId, machineId, boomId, boomName) {
            return `${nexatMachineId}-${machineId}-${boomId}-${boomName}`
        },
        findParticipantForAutoLoad(nexatMachineId = null) {
            const groups = Object.entries(this.machinesAndBooms)
            const filtered = nexatMachineId != null && nexatMachineId !== ''
                ? groups.filter(([id]) => String(id) === String(nexatMachineId))
                : groups

            for (const [groupId, items] of filtered) {
                const item = items.find((entry) => String(entry.machineId) !== '0')
                if (item) {
                    return { nexatMachineId: groupId, item }
                }
            }
            return null
        },
        async tryAutoLoadAsAppliedMap(fieldId, taskId, taskStatus, nexatMachineId = null) {
            if (this.isUnmounted) return false

            const match = this.findParticipantForAutoLoad(nexatMachineId)
            if (!match) return false

            return this.getAsAppliedMapEntries(
                fieldId,
                taskId,
                match.nexatMachineId,
                match.item.machineId,
                match.item.boomId,
                match.item.implementName,
                match.item.boomName,
                taskStatus,
            )
        },
        async getAsAppliedMapEntries(fieldId, taskId, nexatMachineId, machineId, boomId, implementName, boomName, taskStatus = null) {
            const demo = useDemoStore()
            if (demo.isBlocked('as_applied')) return false

            if (this.isUnmounted) return false

            this.mapLoading = true
            this.mapLoadingKey = this.mapEntryKey(nexatMachineId, machineId, boomId, boomName)
            try {
                const res = await getAsAppliedMapEntriesFromTask(fieldId, taskId, machineId, boomId)
                if (this.isUnmounted) return false

                if (!res.ok) {
                    this.clearMap()
                    return false
                }
                if (!res.data?.features?.length) {
                    this.clearMap()
                    return true
                }
                this.featureCollection = res.data
                this.activeFilter = {
                    fieldId,
                    taskId,
                    nexatMachineId,
                    machineId,
                    boomId,
                    implementName,
                    boomName,
                    taskStatus,
                }
                const shown = this.showOnMap({ fitBounds: true })
                if (shown) {
                    await useVehiclesLiveStore().refreshSingleLiveVehicleSilent(nexatMachineId)
                    if (!AS_APPLIED_POLL_ONLY_WHEN_RUNNING || taskStatus === 'running') {
                        this.startPolling()
                    } else {
                        this.stopPolling()
                    }
                }
                return shown
            } catch (error) {
                console.error('Error getting as-applied map entries from task:', error)
                this.clearMap()
                return false
            } finally {
                this.mapLoading = false
                this.mapLoadingKey = null
            }
        },
        buildAsAppliedTooltipHtml(properties = {}) {
            const formatNum = (value) => {
                const display = toDisplayRate(value, this.isWorkingSpeed)
                return display !== null ? display.toFixed(2) : (value ?? '-')
            }

            return `
                <div style="max-width:280px;line-height:1.35;">
                    <div>
                        <span style="opacity:.8">${i18n.global.t('Color rate')}</span>:
                        <b>
                            <span style="display:inline-block;width:10px;height:10px;background:${getRateHex(properties.rate, this.featureCollection?.min_rate, this.featureCollection?.max_rate)}"></span>
                        </b>
                    </div>
                    <div><span style="opacity:.8">${i18n.global.t('Applied rate')}</span>: <b>${formatNum(properties.rate)}</b></div>
                    <div><span style="opacity:.8">${i18n.global.t('Average rate')}</span>: <b>${formatNum(properties.target_rate)}</b></div>
                    <div><span style="opacity:.8">${i18n.global.t('Machine')}</span>: <b>${properties.nexat_machine_id ?? '-'}</b></div>
                    <div><span style="opacity:.8">${i18n.global.t('Task')}</span>: <b>${properties.isobustask_id ?? '-'}</b></div>
                    <div>
                        <span style="opacity:.8">${i18n.global.t('Time')}</span>: 
                        <b>
                            <span>${formatDate(properties.bucket).date}</span>
                            <span>${formatDate(properties.bucket).time}</span>
                            <span>(UTC)</span>
                        </b>
                    </div>
                </div>
            `
        },
        showOnMap({ fitBounds = true } = {}) {
            if (this.isUnmounted) return false

            const mapStore = useMaps()
            const fls = useFieldLayersStore()
            const yieldMap = useYieldMap()
            const isoPreview = useIsoPreview()

            if (!mapStore.map || !mapStore.google || !this.featureCollection?.features?.length) {
                return false
            }

            yieldMap.clearYieldHeatmap(true)
            isoPreview.clearPreview(true)
            fls.toggleFieldLayersVisibility(false)

            const data = this.featureCollection

            this.layer = new GeoJsonLayer({
                id: 'as-applied-map-layer',
                data,
                filled: true,
                stroked: false,
                pickable: true,
                getFillColor: (f) => hexToRgba(
                    getRateHex(f.properties?.rate, data.min_rate, data.max_rate),
                    0.8,
                ),
                autoHighlight: false,
                highlightColor: [255, 255, 255, 191],
                onHover: (info) => {
                    this.hoverFeature = info?.object || null
                },
            })

            const getTooltip = ({ object }) => {
                const feature = object || this.hoverFeature
                if (!feature?.properties) return null
                return { html: this.buildAsAppliedTooltipHtml(feature.properties) }
            }

            if (!this.overlay) {
                this.overlay = new GoogleMapsOverlay({
                    layers: [this.layer],
                    controller: false,
                    getTooltip,
                    style: { zIndex: 1000 },
                })
                this.overlay.setMap(mapStore.map)
            } else {
                this.overlay.setProps({ layers: [this.layer], getTooltip })
            }

            if (fitBounds) {
                const bounds = new mapStore.google.maps.LatLngBounds()

                data.features.forEach((feature) => {
                    const geom = feature?.geometry
                    if (!geom) return

                    if (geom.type === 'Polygon') {
                        geom.coordinates.forEach((ring) => {
                            ring.forEach(([lng, lat]) => bounds.extend({ lat, lng }))
                        })
                    } else if (geom.type === 'MultiPolygon') {
                        geom.coordinates.forEach((polygon) => {
                            polygon.forEach((ring) => {
                                ring.forEach(([lng, lat]) => bounds.extend({ lat, lng }))
                            })
                        })
                    }
                })

                if (!bounds.isEmpty()) {
                    mapStore.map.fitBounds(bounds, { padding: 30 })
                }
            }

            this.visible = true
            return true
        },
        async refreshAsAppliedSilent() {
            const filter = this.activeFilter
            if (!filter || this.isUnmounted) return false

            try {
                const res = await getAsAppliedMapEntriesFromTask(
                    filter.fieldId,
                    filter.taskId,
                    filter.machineId,
                    filter.boomId,
                )
                if (this.isUnmounted || !this.activeFilter) return false

                if (!res.ok || !res.data?.features?.length) {
                    if (this.overlay) {
                        this.overlay.setMap(null)
                        this.overlay = null
                    }
                    this.layer = null
                    this.featureCollection = null
                    this.visible = false
                    return false
                }

                this.featureCollection = res.data
                return this.showOnMap({ fitBounds: false })
            } catch (error) {
                console.error('Error refreshing as-applied map:', error)
                return false
            }
        },
        async tickPoll() {
            if (this.isUnmounted || !this.activeFilter || this.pollInFlight) return
            if (AS_APPLIED_POLL_ONLY_WHEN_RUNNING && this.activeFilter.taskStatus !== 'running') {
                this.stopPolling()
                return
            }

            this.pollInFlight = true
            try {
                const serial = this.activeFilter.nexatMachineId
                await Promise.all([
                    this.refreshAsAppliedSilent(),
                    useVehiclesLiveStore().refreshSingleLiveVehicleSilent(serial),
                ])
            } finally {
                this.pollInFlight = false
            }
        },
        startPolling() {
            this.stopPolling()
            if (!this.activeFilter || this.isUnmounted) return
            if (AS_APPLIED_POLL_ONLY_WHEN_RUNNING && this.activeFilter.taskStatus !== 'running') return

            this.pollIntervalId = setInterval(() => {
                this.tickPoll()
            }, AS_APPLIED_POLL_INTERVAL_MS)
        },
        stopPolling() {
            if (this.pollIntervalId) {
                clearInterval(this.pollIntervalId)
                this.pollIntervalId = null
            }
            this.pollInFlight = false
        },
        clearMap(removeLegend = false) {
            const fls = useFieldLayersStore()
            const serial = this.activeFilter?.nexatMachineId

            this.stopPolling()
            useVehiclesLiveStore().clearSingleLiveVehicle(serial)

            if (this.overlay) {
                this.overlay.setMap(null)
            }
            this.overlay = null
            this.layer = null
            this.featureCollection = null
            this.activeFilter = null
            this.hoverFeature = null
            this.visible = false
            this.mapLoading = false
            this.mapLoadingKey = null

            if (removeLegend && this.legendDraggable) {
                this.legendDraggable.disable()
            }
            fls.toggleFieldLayersVisibility(true)
        },
        clearMachinesAndBooms() {
            this.machinesAndBooms = {}
        },
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAsAppliedMap, import.meta.hot))
}
